<template>
  <div class="tree-list">
    <a-tree
      v-model:selectedKeys="selectedKeys"
      v-model:expandedKeys="expandedKeys"
      :load-data="onLoadData"
      :tree-data="treeData"
      :field-names="props.fieldNames"
      @select="itemSelect"
      :show-line="{ showLeafIcon: false }"
    >
      <template #switcherIcon="{ switcherCls }"><CaretDownFilled :class="switcherCls" /></template>
      <template #title="item">
        <div class="flex-b tree-item">
          <div class="show1Row mr-4 flex-s">
            <slot name="item" :item="item.data"></slot>
          </div>
          <div
            class="right-item"
            :style="{
              display: props.hideMenu ? null : 'block'
            }"
          >
            <slot name="optionItem" :item="item.data"></slot>
          </div>
          <dropDownMenu
            v-if="props.hideMenu"
            :options="props.options"
            :data="item.data"
          ></dropDownMenu>
        </div>
      </template>
    </a-tree>
  </div>
</template>

<script setup>
import { Tree as ATree } from 'ant-design-vue'
import { nextTick, onUnmounted, ref, watch } from 'vue'
import { CaretDownFilled } from '@ant-design/icons-vue'
import dropDownMenu from './dropDownMenu.vue'
import { debounce } from 'lodash'
import eventsBus from '@/utils/eventsBus'

const props = defineProps({
  value: {
    type: Array,
    default: () => []
  },
  data: {
    type: Array,
    default: () => []
  },
  fieldNames: {
    type: Object,
    default: () => ({ children: 'children', title: 'name', key: 'id' })
  },
  options: {
    type: Array,
    default: () => []
  },
  selectList: {
    type: Array,
    default: () => []
  },
  hideMenu: {
    type: Boolean,
    default: true
  }
})
const emits = defineEmits(['update:value', 'update:selectList', 'loadData'])

const treeData = ref([...props.data])
const selectedKeys = ref([...props.selectList])
const expandedKeys = ref([])

const itemSelect = debounce((e, w) => {
  if (!selectedKeys.value[0]?.length) {
    selectedKeys.value[0] = w.node.id
  }
  let parents = []
  if (e.length > 0) {
    getParents(w.node, parents)
    emits('update:value', parents.reverse())
  } else {
    emits('update:value', parents)
  }
  if (expandedKeys.value.includes(w.node.id)) {
    expandedKeys.value = expandedKeys.value.filter((key) => key !== w.node.id)
  } else {
    expandedKeys.value.push(w.node.id)
  }
  emits('update:selectList', selectedKeys.value)
}, 300)
// 定义事件处理函数，以便能够移除
const setNodesWithChildrenHandler = (array) => {
  expandedKeys.value = []
  for (const item of array) {
    expandedKeys.value.push(item.id)
  }
  if (array.length > 0 && array[array.length - 1].children?.length > 0) {
    selectedKeys.value[0] = array[array.length - 1].children[0].id
  }
}
const getParents = (obj, arr = []) => {
  arr.push({
    ...(obj.dataRef || obj.node)
  })
  if (obj.parent) {
    getParents(obj.parent, arr)
  }
}
const onLoadData = (treeNode) => {
  return new Promise((resolve) => {
    emits('loadData', treeNode, (arr) => {
      treeNode.dataRef.children = [...arr]
      treeData.value = [...treeData.value]
      resolve()
    })
  })
}
// 递归函数，用于查找指定 id 的节点并添加数据
const addDataToNodeById = (tree, nodeId, newData) => {
  for (const node of tree) {
    if (node.id === nodeId && !node.children?.length) {
      node['children'] = newData
      return true
    }
    if (node.children && node.children.length > 0) {
      if (addDataToNodeById(node.children, nodeId, newData)) {
        return true
      }
    }
  }
  return false
}
// 递归函数，用于查找指定 id 的节点及其所有上级节点
const findNodeAndParents = (tree, nodeId) => {
  let result = []
  function traverse(node, path) {
    path.push(node)
    if (node.id === nodeId) {
      result = [...path]
      return true
    }
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        if (traverse(child, path)) {
          return true
        }
      }
    }
    path.pop()
    return false
  }
  for (const root of tree) {
    if (traverse(root, [])) {
      break
    }
  }

  return result
}
const openNode = ({ parentId, id }) => {
  emits('loadData', { id: parentId }, (arr) => {
    addDataToNodeById(treeData.value, parentId, arr)
    expandedKeys.value.push(parentId)
    nextTick(() => {
      const arr = findNodeAndParents(treeData.value, id)
      selectedKeys.value = [id]
      emits('update:selectList', selectedKeys.value)
      emits('update:value', arr)
    })
  })
}

watch(
  () => props.data,
  (val) => {
    treeData.value = val
  },
  {
    deep: true
  }
)

defineExpose({
  openNode
})
eventsBus.on('setNodesWithChildren', setNodesWithChildrenHandler)
// 在组件卸载时移除事件监听
onUnmounted(() => {
  eventsBus.off('setNodesWithChildren', setNodesWithChildrenHandler)
})
</script>

<style lang="less" scoped>
.tree-list {
  // overflow-y: auto;
  // overflow: hidden;
  height: 100%;
  background-color: #fff;
}
:deep(.item-options) {
  display: none;
  padding: 0 4px;
}
.right-item {
  display: block;
}
.tree-item {
  min-width: 100px;
  &:hover {
    :deep(.item-options) {
      display: block;
    }
    .right-item {
      display: none;
    }
  }
}
:deep(.item-options),
.right-item {
  position: sticky;
  right: 0;
  background-color: #fff;
  padding-right: 4px;
}
.menu {
  color: #000;
  background-color: #fff;
  padding: 6px;
  .menu-item {
    min-width: 100px;
    padding: 2px 6px;
    cursor: pointer;
    border-radius: 6px;
    &:hover {
      background-color: @theme-color-1;
    }
  }
}
:deep(.ant-tree .ant-tree-treenode) {
  width: 100%;
}
:deep(.ant-tree .ant-tree-node-content-wrapper) {
  flex: 1;
  padding-right: 0;
}
:deep(.ant-tree .ant-tree-list-holder-inner) {
  width: max-content;
  width: 100%;
}
</style>
